import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import type { FormHTMLAttributes, ReactNode } from "react";

import { PasswordInput } from "@/components/password-input";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Input, type InputProps } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

export { fieldContext, formContext, useFieldContext, useFormContext };

interface FieldShellProps {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
  children: ReactNode;
}

interface TextFieldProps extends Omit<InputProps, "id" | "name" | "value" | "onBlur" | "onChange"> {
  label?: ReactNode;
  description?: ReactNode;
  fieldClassName?: string;
}

interface TextareaFieldProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id" | "name" | "value" | "onBlur" | "onChange"
> {
  label?: ReactNode;
  description?: ReactNode;
  fieldClassName?: string;
}

interface CheckboxFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "id" | "name" | "type" | "checked" | "value" | "onBlur" | "onChange"
> {
  label: ReactNode;
  description?: ReactNode;
  fieldClassName?: string;
}

interface SubmitButtonProps extends ButtonProps {
  submittingLabel?: ReactNode;
}

interface FieldWithErrors {
  state: {
    meta: {
      errors: unknown[];
    };
  };
}

function getFieldErrorMessages(field: FieldWithErrors): string[] {
  return field.state.meta.errors
    .map((error: unknown) => {
      if (typeof error === "string") {
        return error;
      }

      return (error as { message?: string })?.message;
    })
    .filter((message): message is string => Boolean(message));
}

function hasFieldErrors(field: FieldWithErrors): boolean {
  return getFieldErrorMessages(field).length > 0;
}

export function FieldErrors({ className }: { className?: string }) {
  const field = useFieldContext<unknown>();
  const messages = getFieldErrorMessages(field);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-1", className)}>
      {messages.map((message) => (
        <p key={message} className="text-destructive text-sm">
          {message}
        </p>
      ))}
    </div>
  );
}

function FieldShell({ label, description, className, children }: FieldShellProps) {
  const field = useFieldContext<unknown>();

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label htmlFor={field.name}>{label}</Label>}
      {children}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <FieldErrors />
    </div>
  );
}

function TextField({ label, description, fieldClassName, className, ...props }: TextFieldProps) {
  const field = useFieldContext<string>();

  return (
    <FieldShell label={label} description={description} className={fieldClassName}>
      <Input
        {...props}
        id={field.name}
        name={field.name}
        value={field.state.value ?? ""}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
        aria-invalid={hasFieldErrors(field)}
        className={className}
      />
    </FieldShell>
  );
}

function PasswordField({
  label,
  description,
  fieldClassName,
  className,
  ...props
}: TextFieldProps) {
  const field = useFieldContext<string>();

  return (
    <FieldShell label={label} description={description} className={fieldClassName}>
      <PasswordInput
        {...props}
        id={field.name}
        name={field.name}
        value={field.state.value ?? ""}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
        aria-invalid={hasFieldErrors(field)}
        className={className}
      />
    </FieldShell>
  );
}

function TextareaField({
  label,
  description,
  fieldClassName,
  className,
  ...props
}: TextareaFieldProps) {
  const field = useFieldContext<string>();

  return (
    <FieldShell label={label} description={description} className={fieldClassName}>
      <Textarea
        {...props}
        id={field.name}
        name={field.name}
        value={field.state.value ?? ""}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
        aria-invalid={hasFieldErrors(field)}
        className={className}
      />
    </FieldShell>
  );
}

function CheckboxField({
  label,
  description,
  fieldClassName,
  className,
  ...props
}: CheckboxFieldProps) {
  const field = useFieldContext<boolean>();

  return (
    <FieldShell className={fieldClassName}>
      <label className="flex items-start gap-3 cursor-pointer group max-sm:min-h-11">
        <input
          {...props}
          id={field.name}
          name={field.name}
          type="checkbox"
          checked={field.state.value ?? false}
          onBlur={field.handleBlur}
          onChange={(event) => field.handleChange(event.target.checked)}
          aria-invalid={hasFieldErrors(field)}
          className={cn("mt-1 border-border text-primary focus:ring-primary", className)}
        />
        <span>
          <span className="text-sm font-medium group-hover:text-primary transition-colors">
            {label}
          </span>
          {description && (
            <span className="block text-xs text-muted-foreground">{description}</span>
          )}
        </span>
      </label>
    </FieldShell>
  );
}

function Form({ children, onSubmit, ...props }: FormHTMLAttributes<HTMLFormElement>) {
  const appForm = useFormContext();

  return (
    <form
      {...props}
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSubmit?.(event);
        void appForm.handleSubmit();
      }}
    >
      {children}
    </form>
  );
}

function SubmitButton({ children, submittingLabel, disabled, ...props }: SubmitButtonProps) {
  const appForm = useFormContext();

  return (
    <appForm.Subscribe>
      {(state) => (
        <Button
          type="submit"
          disabled={disabled || !state.canSubmit || state.isSubmitting}
          {...props}
        >
          {state.isSubmitting ? (submittingLabel ?? children) : children}
        </Button>
      )}
    </appForm.Subscribe>
  );
}

export const { useAppForm, withForm, withFieldGroup } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField,
    PasswordField,
    TextareaField,
    CheckboxField,
    FieldErrors,
  },
  formComponents: {
    Form,
    SubmitButton,
  },
});
